
import { CollisionEvent, SimulationConfig } from '../types';
import { seedRandom, lerpColor } from './BreakUtils';

export const drawCollisionEffect = (
    ctx: CanvasRenderingContext2D,
    e: CollisionEvent,
    config: SimulationConfig,
    globalHue: number,
    canvasWidth?: number,
    canvasHeight?: number,
    camera?: { x: number, y: number, scale: number }
) => {
    const elapsed = Date.now() - e.startTime;
    const duration = config.collisionEffectDuration || 800;
    if (elapsed > duration) return;
    
    const progress = elapsed / duration;
    const type = e.effectType || 'RIPPLE';
    const particles = config.collisionEffectParticleCount || 20;
    const gravity = config.collisionEffectGravity || 0.5;
    
    ctx.save();
    
    const determineColor = (idx: number, total: number) => {
        const mode = config.collisionEffectColorMode;
        if (mode === 'RAINBOW') return `hsl(${(globalHue + idx * 10) % 360}, 100%, 60%)`;
        if (mode === 'CUSTOM_SOLID') return config.collisionEffectColor1;
        if (mode === 'CUSTOM_GRADIENT') return lerpColor(config.collisionEffectColor1, config.collisionEffectColor2, idx/total);
        return e.color; 
    };

    switch(type) {
        case 'RIPPLE': {
            ctx.beginPath();
            ctx.arc(e.pos.x, e.pos.y, e.radius * (1 + progress * 4), 0, Math.PI * 2);
            ctx.strokeStyle = determineColor(0, 1);
            ctx.lineWidth = 4 * (1 - progress);
            ctx.globalAlpha = 1 - progress;
            ctx.stroke();
            break;
        }
        case 'GRAVITY_PARTICLES': {
            for(let i=0; i<particles; i++) {
                const rnd = seedRandom(e.id, i);
                const angle = rnd * Math.PI * 2;
                const speed = (2 + seedRandom(e.id, i+100) * 8);
                const vx = Math.cos(angle) * speed;
                const vy = Math.sin(angle) * speed;
                
                const t = elapsed / 16.6; 
                const x = e.pos.x + vx * t;
                const y = e.pos.y + vy * t + 0.5 * gravity * t * t; 
                
                ctx.fillStyle = determineColor(i, particles);
                ctx.globalAlpha = 1 - progress;
                const size = 3 + (1-progress) * 2;
                ctx.beginPath(); ctx.arc(x, y, size, 0, Math.PI*2); ctx.fill();
            }
            break;
        }
        case 'ORBITAL_SPIN': {
            for(let i=0; i<particles; i++) {
                const rnd = seedRandom(e.id, i);
                const angleBase = rnd * Math.PI * 2;
                const rotation = progress * Math.PI * 4 * (i%2===0?1:-1);
                const dist = (20 + rnd * 100) * progress;
                
                const x = e.pos.x + Math.cos(angleBase + rotation) * dist;
                const y = e.pos.y + Math.sin(angleBase + rotation) * dist;
                
                ctx.strokeStyle = determineColor(i, particles);
                ctx.globalAlpha = 1 - progress;
                ctx.lineWidth = 2;
                ctx.beginPath(); ctx.moveTo(x, y); 
                ctx.lineTo(x - Math.cos(angleBase+rotation)*10, y - Math.sin(angleBase+rotation)*10);
                ctx.stroke();
            }
            break;
        }
        case 'ELASTIC_RING': {
            ctx.strokeStyle = determineColor(0,1);
            ctx.lineWidth = 3;
            ctx.globalAlpha = 1 - progress;
            ctx.beginPath();
            const baseR = e.radius * 2 * (1 + progress);
            for(let a=0; a<=Math.PI*2; a+=0.1) {
                const wob = Math.sin(a * 10 + elapsed * 0.02) * 20 * (1-progress);
                const r = baseR + wob;
                const x = e.pos.x + Math.cos(a) * r;
                const y = e.pos.y + Math.sin(a) * r;
                if(a===0) ctx.moveTo(x,y); else ctx.lineTo(x,y);
            }
            ctx.closePath(); ctx.stroke();
            break;
        }
        case 'LIQUID_SPLASH': {
            for(let i=0; i<particles; i++) {
                const rnd = seedRandom(e.id, i);
                const angle = -Math.PI/2 + (rnd - 0.5) * Math.PI; 
                const speed = 5 + rnd * 10;
                const t = elapsed / 16.6;
                const x = e.pos.x + Math.cos(angle) * speed * t;
                const y = e.pos.y + Math.sin(angle) * speed * t + 0.5 * gravity * t * t;
                
                const stretch = Math.atan2(Math.sin(angle)*speed + gravity*t, Math.cos(angle)*speed);
                ctx.fillStyle = determineColor(i, particles);
                ctx.globalAlpha = (1-progress);
                ctx.save(); ctx.translate(x,y); ctx.rotate(stretch);
                ctx.beginPath(); ctx.ellipse(0,0, 6*(1-progress), 2*(1-progress), 0, 0, Math.PI*2);
                ctx.fill(); ctx.restore();
            }
            break;
        }
        case 'SPARKS': {
            for(let i=0; i<particles; i++) {
                const rnd = seedRandom(e.id, i);
                const angle = rnd * Math.PI * 2;
                const speed = 15 * rnd;
                const dist = speed * elapsed * 0.1;
                const x = e.pos.x + Math.cos(angle) * dist;
                const y = e.pos.y + Math.sin(angle) * dist;
                
                ctx.strokeStyle = determineColor(i, particles);
                ctx.globalAlpha = Math.max(0, 1 - progress * 1.5); 
                ctx.lineWidth = 1;
                ctx.beginPath(); ctx.moveTo(x, y);
                ctx.lineTo(x - Math.cos(angle)*15, y - Math.sin(angle)*15);
                ctx.stroke();
            }
            break;
        }
        case 'SHATTER_GLASS': {
            for(let i=0; i<15; i++) {
                const rnd = seedRandom(e.id, i);
                const angle = rnd * Math.PI * 2;
                const speed = 3 + rnd * 5;
                const t = elapsed/16.6;
                const x = e.pos.x + Math.cos(angle)*speed*t;
                const y = e.pos.y + Math.sin(angle)*speed*t + 0.5 * gravity * t*t;
                const rot = t * (rnd - 0.5);
                
                ctx.fillStyle = determineColor(i, 15);
                ctx.globalAlpha = 1 - progress;
                ctx.save(); ctx.translate(x,y); ctx.rotate(rot);
                ctx.beginPath(); ctx.moveTo(-5,-5); ctx.lineTo(5,0); ctx.lineTo(0,5); ctx.fill();
                ctx.restore();
            }
            break;
        }
        case 'PIXEL_CRUMBLE': {
            const size = 8;
            for(let i=0; i<particles; i++) {
                const rnd = seedRandom(e.id, i);
                const angle = rnd * Math.PI * 2;
                const dist = 20 + progress * 50;
                const t = elapsed/16.6;
                const x = e.pos.x + Math.cos(angle) * dist;
                const y = e.pos.y + Math.sin(angle) * dist + 0.5 * gravity * t*t; 
                
                ctx.fillStyle = determineColor(i, particles);
                ctx.globalAlpha = 1 - progress;
                ctx.fillRect(x - size/2, y - size/2, size, size);
            }
            break;
        }
        case 'NEON_PULSE': {
            ctx.globalAlpha = 1 - progress;
            ctx.shadowBlur = 50 * (1-progress);
            ctx.shadowColor = determineColor(0,1);
            ctx.strokeStyle = '#fff';
            ctx.lineWidth = 5;
            ctx.beginPath(); ctx.arc(e.pos.x, e.pos.y, e.radius + progress * 100, 0, Math.PI*2);
            ctx.stroke(); ctx.shadowBlur = 0;
            break;
        }
        case 'VORTEX_SUCK': {
            const invP = 1 - progress;
            for(let i=0; i<particles; i++) {
                const rnd = seedRandom(e.id, i);
                const angle = rnd * Math.PI * 2 + progress * 5;
                const dist = 100 * invP * invP;
                const x = e.pos.x + Math.cos(angle) * dist;
                const y = e.pos.y + Math.sin(angle) * dist;
                
                ctx.fillStyle = determineColor(i, particles);
                ctx.globalAlpha = progress; 
                ctx.beginPath(); ctx.arc(x,y, 3, 0, Math.PI*2); ctx.fill();
            }
            break;
        }
        case 'FIREWORKS': {
            const t = elapsed / 16.6;
            for(let i=0; i<particles; i++) {
                const rnd = seedRandom(e.id, i);
                const angle = rnd * Math.PI * 2;
                const speed = 4 + rnd * 4;
                const x = e.pos.x + Math.cos(angle) * speed * t;
                const y = e.pos.y + Math.sin(angle) * speed * t + 0.5 * 0.1 * t*t; 
                
                ctx.fillStyle = determineColor(i, particles);
                ctx.globalAlpha = 1 - progress;
                ctx.beginPath(); ctx.arc(x,y, 2, 0, Math.PI*2); ctx.fill();
            }
            break;
        }
        case 'SMOKE_PUFF': {
            for(let i=0; i<10; i++) {
                const rnd = seedRandom(e.id, i);
                const angle = rnd * Math.PI * 2;
                const dist = progress * 60 * rnd;
                const x = e.pos.x + Math.cos(angle) * dist;
                const y = e.pos.y + Math.sin(angle) * dist - progress * 30; 
                
                ctx.fillStyle = determineColor(i, 10);
                ctx.globalAlpha = (1-progress) * 0.5;
                ctx.beginPath(); ctx.arc(x,y, 10 + progress * 20, 0, Math.PI*2); ctx.fill();
            }
            break;
        }
        case 'GLITCH_FRAGMENTS': {
            for(let i=0; i<particles; i++) {
                const rnd = seedRandom(e.id, i);
                if(rnd > 0.5) continue;
                const xOff = (rnd - 0.5) * 100 * progress;
                const yOff = (seedRandom(e.id, i+50) - 0.5) * 100 * progress;
                ctx.fillStyle = determineColor(i, particles);
                ctx.globalAlpha = 1 - progress;
                ctx.fillRect(e.pos.x + xOff, e.pos.y + yOff, 10 * (1-progress), 4);
            }
            break;
        }
        case 'LIGHTNING_BOLT': {
            if (progress > 0.3) return; 
            const count = 5;
            for(let i=0; i<count; i++) {
                const angle = (i/count) * Math.PI * 2;
                ctx.strokeStyle = determineColor(i, count);
                ctx.lineWidth = 2;
                ctx.beginPath(); ctx.moveTo(e.pos.x, e.pos.y);
                let cx = e.pos.x, cy = e.pos.y;
                for(let j=0; j<5; j++) {
                    cx += Math.cos(angle + (Math.random()-0.5)) * 30;
                    cy += Math.sin(angle + (Math.random()-0.5)) * 30;
                    ctx.lineTo(cx, cy);
                }
                ctx.stroke();
            }
            break;
        }
        case 'BUBBLE_POP': {
            for(let i=0; i<8; i++) {
                const rnd = seedRandom(e.id, i);
                const angle = rnd * Math.PI * 2;
                const dist = progress * 80;
                const x = e.pos.x + Math.cos(angle) * dist;
                const y = e.pos.y + Math.sin(angle) * dist;
                
                ctx.strokeStyle = determineColor(i, 8);
                ctx.globalAlpha = 1 - progress;
                ctx.lineWidth = 1;
                ctx.beginPath(); ctx.arc(x,y, 5 + progress*10, 0, Math.PI*2); ctx.stroke();
            }
            break;
        }
        case 'SPIRAL_GALAXY': {
            for(let i=0; i<particles; i++) {
                const rnd = seedRandom(e.id, i);
                const arm = Math.floor(rnd * 3);
                const p = progress;
                const r = p * 100 + rnd * 20;
                const theta = p * 5 + (arm * Math.PI * 2 / 3);
                
                const x = e.pos.x + Math.cos(theta) * r;
                const y = e.pos.y + Math.sin(theta) * r;
                
                ctx.fillStyle = determineColor(i, particles);
                ctx.globalAlpha = 1 - progress;
                ctx.beginPath(); ctx.arc(x,y, 2, 0, Math.PI*2); ctx.fill();
            }
            break;
        }
        case 'SONIC_WAVE': {
            for(let i=0; i<5; i++) {
                const r = e.radius + progress * 200 + i * 20;
                ctx.beginPath(); ctx.arc(e.pos.x, e.pos.y, r, 0, Math.PI*2);
                ctx.strokeStyle = determineColor(i, 5);
                ctx.lineWidth = 2;
                ctx.globalAlpha = (1-progress) / (i+1);
                ctx.stroke();
            }
            break;
        }
        case 'QUANTUM_TUNNEL': {
            const count = 12;
            for(let i=0; i<count; i++) {
                const angle = (i/count) * Math.PI * 2;
                const startR = 100 * (1-progress);
                const endR = 200 * (1-progress);
                ctx.strokeStyle = determineColor(i, count);
                ctx.globalAlpha = progress;
                ctx.beginPath(); 
                ctx.moveTo(e.pos.x + Math.cos(angle)*startR, e.pos.y + Math.sin(angle)*startR);
                ctx.lineTo(e.pos.x + Math.cos(angle)*endR, e.pos.y + Math.sin(angle)*endR);
                ctx.stroke();
            }
            break;
        }
        case 'DNA_HELIX': {
            const h = 100 * progress;
            for(let i=0; i<20; i++) {
                const yOff = (i/20) * h - h/2;
                const w = 20 * Math.sin(i * 0.5 + elapsed * 0.01);
                ctx.fillStyle = determineColor(i, 20);
                ctx.globalAlpha = 1 - progress;
                ctx.beginPath(); ctx.arc(e.pos.x + w, e.pos.y + yOff, 3, 0, Math.PI*2); ctx.fill();
                ctx.beginPath(); ctx.arc(e.pos.x - w, e.pos.y + yOff, 3, 0, Math.PI*2); ctx.fill();
            }
            break;
        }
        case 'MATRIX_RAIN': {
            for(let i=0; i<15; i++) {
                const rnd = seedRandom(e.id, i);
                const x = e.pos.x + (rnd - 0.5) * 60;
                const y = e.pos.y + progress * 100 * (0.5 + rnd);
                ctx.fillStyle = determineColor(i, 15);
                ctx.globalAlpha = 1 - progress;
                ctx.font = "10px monospace";
                ctx.fillText(String.fromCharCode(0x30A0 + Math.floor(rnd * 96)), x, y);
            }
            break;
        }
        case 'PLASMA_BLOB': {
            ctx.fillStyle = determineColor(0, 1);
            ctx.globalAlpha = (1-progress) * 0.6;
            ctx.beginPath();
            for(let a=0; a<=Math.PI*2; a+=0.2) {
                const r = e.radius * 2 + Math.sin(a*5 + elapsed*0.01) * 20 * (1-progress);
                const x = e.pos.x + Math.cos(a) * r;
                const y = e.pos.y + Math.sin(a) * r;
                ctx.lineTo(x,y);
            }
            ctx.fill();
            break;
        }
        case 'CRYSTAL_SHARDS': {
            for(let i=0; i<10; i++) {
                const rnd = seedRandom(e.id, i);
                const angle = rnd * Math.PI * 2;
                const dist = progress * 100;
                const cx = e.pos.x + Math.cos(angle) * dist;
                const cy = e.pos.y + Math.sin(angle) * dist;
                
                ctx.fillStyle = determineColor(i, 10);
                ctx.globalAlpha = 1 - progress;
                ctx.save(); ctx.translate(cx, cy); ctx.rotate(angle + progress);
                ctx.beginPath(); ctx.moveTo(0, -10); ctx.lineTo(5, 0); ctx.lineTo(0, 10); ctx.lineTo(-5, 0); ctx.fill();
                ctx.restore();
            }
            break;
        }
        case 'GHOST_SOULS': {
            for(let i=0; i<5; i++) {
                const rnd = seedRandom(e.id, i);
                const angle = rnd * Math.PI * 2;
                const dist = progress * 50;
                const wobble = Math.sin(elapsed * 0.02 + i);
                const x = e.pos.x + Math.cos(angle) * dist + wobble * 10;
                const y = e.pos.y + Math.sin(angle) * dist - progress * 50; 
                
                ctx.fillStyle = determineColor(i, 5);
                ctx.globalAlpha = (1-progress) * 0.5;
                ctx.beginPath(); ctx.arc(x, y, 6, 0, Math.PI*2); ctx.fill();
            }
            break;
        }
        case 'CYBER_HEX': {
            for(let i=0; i<6; i++) {
                const angle = (i/6) * Math.PI * 2 + progress;
                const dist = progress * 80;
                const size = 15 * (1-progress);
                const cx = e.pos.x + Math.cos(angle) * dist;
                const cy = e.pos.y + Math.sin(angle) * dist;
                
                ctx.strokeStyle = determineColor(i, 6);
                ctx.lineWidth = 2;
                ctx.globalAlpha = 1 - progress;
                ctx.beginPath();
                for(let j=0; j<6; j++) {
                    const ja = (j/6) * Math.PI * 2;
                    ctx.lineTo(cx + Math.cos(ja)*size, cy + Math.sin(ja)*size);
                }
                ctx.closePath(); ctx.stroke();
            }
            break;
        }
        case 'INK_SPLAT': {
            const count = 12;
            ctx.fillStyle = determineColor(0,1);
            ctx.globalAlpha = 1 - progress;
            for(let i=0; i<count; i++) {
                const rnd = seedRandom(e.id, i);
                const angle = (i/count) * Math.PI * 2;
                const len = e.radius + rnd * 100 * Math.pow(progress, 0.5);
                const width = 10 * (1-progress) * rnd;
                
                ctx.beginPath(); 
                ctx.moveTo(e.pos.x, e.pos.y);
                ctx.lineTo(e.pos.x + Math.cos(angle)*len, e.pos.y + Math.sin(angle)*len);
                ctx.lineWidth = width;
                ctx.strokeStyle = ctx.fillStyle;
                ctx.stroke();
                
                // End blob
                ctx.beginPath(); ctx.arc(e.pos.x + Math.cos(angle)*len, e.pos.y + Math.sin(angle)*len, width, 0, Math.PI*2); ctx.fill();
            }
            break;
        }
        case 'SUPERNOVA': {
            if(progress < 0.2) {
                ctx.fillStyle = '#fff';
                ctx.globalAlpha = 1 - (progress/0.2);
                ctx.beginPath(); ctx.arc(e.pos.x, e.pos.y, e.radius * 3, 0, Math.PI*2); ctx.fill();
            }
            ctx.strokeStyle = determineColor(0,1);
            ctx.lineWidth = 10 * (1-progress);
            ctx.globalAlpha = 1 - progress;
            ctx.beginPath(); ctx.arc(e.pos.x, e.pos.y, e.radius + progress * 300, 0, Math.PI*2); ctx.stroke();
            break;
        }
        case 'CONFETTI_CANNON': {
            for(let i=0; i<30; i++) {
                const rnd = seedRandom(e.id, i);
                const angle = -Math.PI/2 + (rnd - 0.5) * Math.PI;
                const speed = 10 + rnd * 10;
                const t = elapsed/16.6;
                const x = e.pos.x + Math.cos(angle) * speed * t;
                const y = e.pos.y + Math.sin(angle) * speed * t + 0.5 * gravity * t*t;
                
                ctx.fillStyle = `hsl(${(rnd * 360)}, 100%, 50%)`;
                ctx.globalAlpha = 1 - progress;
                ctx.save(); ctx.translate(x,y); ctx.rotate(t * rnd);
                ctx.fillRect(-3, -2, 6, 4);
                ctx.restore();
            }
            break;
        }
    }
    
    ctx.restore();
};
